// Quotation to Sales Order Converter
// Handles conversion of quotation data to sales order format

import { formatDateTime } from './formatters';

// Generate sales order from quotation
export const convertQuotationToSalesOrder = (quotation) => {
  try {
    const currentDate = new Date().toISOString().split('T')[0];
    const soNumber = generateSONumber();
  
  // Convert quotation items to sales order items
  const soItems = quotation.items?.map((item, index) => ({
    item_id: `SOI-${soNumber}-${String(index + 1).padStart(3, '0')}`,
    sales_order_id: soNumber,
    product_id: item.product_id || `PROD-${index + 1}`,
    product_name: item.product_name || '未知產品',
    description: item.description || '',
    quantity: item.quantity || 0,
    unit: item.unit || '件',
    unit_price: item.unit_price || 0,
    discount_percentage: item.discount_percentage || 0,
    tax_rate: item.tax_rate || 0,
    subtotal: item.subtotal || (item.quantity * item.unit_price) || 0,
    tax_amount: item.tax_amount || 0,
    total: item.total || item.subtotal || (item.quantity * item.unit_price) || 0,
    shipped_quantity: 0,
    remaining_quantity: item.quantity || 0,
    status: 'pending'
  })) || [];

  // Calculate totals
  const subtotal = soItems.reduce((sum, item) => sum + (item.subtotal || 0), 0);
  const taxAmount = soItems.reduce((sum, item) => sum + (item.tax_amount || 0), 0);
  const totalAmount = subtotal + taxAmount;

  // Create sales order object
  const salesOrder = {
    sales_order_id: soNumber,
    customer_id: quotation.customer_id || 'CUSTOMER-001',
    customer_name: quotation.customer_name || '未知客戶',
    contact_person: quotation.contact_person || '未知聯絡人',
    contact_email: quotation.contact_email || '',
    contact_phone: quotation.contact_phone || '',
    order_date: currentDate,
    expected_delivery_date: addDays(currentDate, 30), // Default 30 days
    payment_terms: 'net30',
    currency: quotation.currency || 'HKD',
    subtotal: subtotal,
    tax_amount: taxAmount,
    total_amount: totalAmount,
    status: 'draft',
    quotation_reference: quotation.quotation_id || quotation.id || 'QT-UNKNOWN',
    notes: `由報價單 ${quotation.quotation_id || quotation.id || 'QT-UNKNOWN'} 轉換而來`,
    internal_notes: quotation.internal_notes || '',
    created_by: quotation.created_by || 'system',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    items: soItems
  };

    return salesOrder;
  } catch (error) {
    console.error('Error converting quotation to sales order:', error);
    throw new Error(`轉換失敗: ${error.message}`);
  }
};

// Generate unique sales order number
const generateSONumber = () => {
  const year = new Date().getFullYear();
  const month = String(new Date().getMonth() + 1).padStart(2, '0');
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  return `SO-${year}-${month}-${random}`;
};

// Add days to date
const addDays = (dateString, days) => {
  const date = new Date(dateString);
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0];
};

// Validate quotation for conversion
export const validateQuotationForConversion = (quotation) => {
  const errors = [];

  if (!quotation) {
    errors.push('報價單資料不存在');
    return errors;
  }

  if (quotation.status_id !== 'ACCEPTED' && quotation.status !== 'ACCEPTED' && 
      quotation.status_id !== 'SO' && quotation.status !== 'SO') {
    errors.push('只有已接受的報價單才能轉換為訂單');
  }

  if (!quotation.items || quotation.items.length === 0) {
    errors.push('報價單必須包含至少一個項目');
  }

  if (!quotation.customer_id && !quotation.customer_name) {
    errors.push('報價單必須包含有效的客戶資料');
  }

  if (!quotation.total_amount || quotation.total_amount <= 0) {
    errors.push('報價單總金額必須大於零');
  }

  return errors;
};

// Update quotation status after conversion
export const updateQuotationStatus = (quotation, newStatus = 'SO') => {
  return {
    ...quotation,
    status_id: newStatus,
    status: newStatus, // Handle both field names
    updated_at: new Date().toISOString(),
    converted_at: new Date().toISOString(),
    converted_to_order: true
  };
};
